import { useQuery, useMutation } from "@tanstack/react-query";
import type { UseQueryOptions, UseMutationOptions } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";
import type { ErrorType } from "./custom-fetch";

export interface MilestoneItem {
  id: number;
  invoiceId: number;
  name: string;
  percentage: number | null | undefined;
  amount: number | null | undefined;
  status: "pending" | "invoiced" | "paid";
  notes: string | null | undefined;
  sortOrder: number;
  childInvoiceId: number | null | undefined;
  completedAt: string | null | undefined;
  createdAt: string;
  updatedAt: string;
}

export interface MilestoneInput {
  id?: number;
  name: string;
  percentage?: number | null;
  amount?: number | null;
  notes?: string | null;
  sortOrder?: number;
}

export interface UpsertMilestonesBody {
  milestones: MilestoneInput[];
}

export interface CompleteMilestoneResult {
  milestone: MilestoneItem;
  childInvoiceId: number;
}

export const getListMilestonesQueryKey = (invoiceId: number) =>
  [`/api/invoices/${invoiceId}/milestones`] as const;

export const listMilestones = async (invoiceId: number): Promise<MilestoneItem[]> =>
  customFetch<MilestoneItem[]>(`/api/invoices/${invoiceId}/milestones`);

export const useGetMilestones = <
  TData = MilestoneItem[],
  TError = ErrorType<unknown>
>(
  invoiceId: number,
  options?: {
    query?: UseQueryOptions<MilestoneItem[], TError, TData>;
  }
) => {
  const { query: queryOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getListMilestonesQueryKey(invoiceId);

  return useQuery<MilestoneItem[], TError, TData>({
    queryKey,
    queryFn: () => listMilestones(invoiceId),
    enabled: !!invoiceId,
    ...queryOptions,
  });
};

export const upsertMilestones = async (
  invoiceId: number,
  data: UpsertMilestonesBody
): Promise<MilestoneItem[]> =>
  customFetch<MilestoneItem[]>(`/api/invoices/${invoiceId}/milestones`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

export const useUpsertMilestones = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: UseMutationOptions<
    MilestoneItem[],
    TError,
    { invoiceId: number; data: UpsertMilestonesBody },
    TContext
  >
) => {
  return useMutation<
    MilestoneItem[],
    TError,
    { invoiceId: number; data: UpsertMilestonesBody },
    TContext
  >({
    mutationFn: ({ invoiceId, data }) => upsertMilestones(invoiceId, data),
    ...options,
  });
};

export const completeMilestone = async (
  invoiceId: number,
  milestoneId: number
): Promise<CompleteMilestoneResult> =>
  customFetch<CompleteMilestoneResult>(
    `/api/invoices/${invoiceId}/milestones/${milestoneId}/complete`,
    { method: "POST" }
  );

export const useCompleteMilestone = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: UseMutationOptions<
    CompleteMilestoneResult,
    TError,
    { invoiceId: number; milestoneId: number },
    TContext
  >
) => {
  return useMutation<
    CompleteMilestoneResult,
    TError,
    { invoiceId: number; milestoneId: number },
    TContext
  >({
    mutationFn: ({ invoiceId, milestoneId }) => completeMilestone(invoiceId, milestoneId),
    ...options,
  });
};

export interface EstimateMilestoneItem {
  id: number;
  estimateId: number;
  name: string;
  percentage: number | null | undefined;
  amount: number | null | undefined;
  notes: string | null | undefined;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface EstimateMilestoneInput {
  id?: number;
  name: string;
  percentage?: number | null;
  amount?: number | null;
  notes?: string | null;
  sortOrder?: number;
}

export interface UpsertEstimateMilestonesBody {
  milestones: EstimateMilestoneInput[];
}

export const getListEstimateMilestonesQueryKey = (estimateId: number) =>
  [`/api/estimates/${estimateId}/milestones`] as const;

export const listEstimateMilestones = async (estimateId: number): Promise<EstimateMilestoneItem[]> =>
  customFetch<EstimateMilestoneItem[]>(`/api/estimates/${estimateId}/milestones`);

export const useGetEstimateMilestones = <
  TData = EstimateMilestoneItem[],
  TError = ErrorType<unknown>
>(
  estimateId: number,
  options?: {
    query?: UseQueryOptions<EstimateMilestoneItem[], TError, TData>;
  }
) => {
  const { query: queryOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getListEstimateMilestonesQueryKey(estimateId);
  return useQuery<EstimateMilestoneItem[], TError, TData>({
    queryKey,
    queryFn: () => listEstimateMilestones(estimateId),
    enabled: !!estimateId,
    ...queryOptions,
  });
};

export const upsertEstimateMilestones = async (
  estimateId: number,
  data: UpsertEstimateMilestonesBody
): Promise<EstimateMilestoneItem[]> =>
  customFetch<EstimateMilestoneItem[]>(`/api/estimates/${estimateId}/milestones`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

export const useUpsertEstimateMilestones = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: UseMutationOptions<
    EstimateMilestoneItem[],
    TError,
    { estimateId: number; data: UpsertEstimateMilestonesBody },
    TContext
  >
) => {
  return useMutation<
    EstimateMilestoneItem[],
    TError,
    { estimateId: number; data: UpsertEstimateMilestonesBody },
    TContext
  >({
    mutationFn: ({ estimateId, data }) => upsertEstimateMilestones(estimateId, data),
    ...options,
  });
};

export const deleteEstimateMilestone = async (
  estimateId: number,
  milestoneId: number
): Promise<void> =>
  customFetch<void>(`/api/estimates/${estimateId}/milestones/${milestoneId}`, {
    method: "DELETE",
  });

export const useDeleteEstimateMilestone = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: UseMutationOptions<
    void,
    TError,
    { estimateId: number; milestoneId: number },
    TContext
  >
) => {
  return useMutation<
    void,
    TError,
    { estimateId: number; milestoneId: number },
    TContext
  >({
    mutationFn: ({ estimateId, milestoneId }) => deleteEstimateMilestone(estimateId, milestoneId),
    ...options,
  });
};

export const deleteMilestone = async (
  invoiceId: number,
  milestoneId: number
): Promise<void> =>
  customFetch<void>(`/api/invoices/${invoiceId}/milestones/${milestoneId}`, {
    method: "DELETE",
  });

export const useDeleteMilestone = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: UseMutationOptions<
    void,
    TError,
    { invoiceId: number; milestoneId: number },
    TContext
  >
) => {
  return useMutation<
    void,
    TError,
    { invoiceId: number; milestoneId: number },
    TContext
  >({
    mutationFn: ({ invoiceId, milestoneId }) => deleteMilestone(invoiceId, milestoneId),
    ...options,
  });
};
